const ASSOCIATED_FIELD_SUFFIX = /_C\d+$/;

export const getMetadataFieldDisplayLabel = (text?: string | null): string => {
  if (!text) {
    return '';
  }

  const match = text.match(/^(.*?)_CI\d+$/);

  return match ? match[1] : text;
};

export const getStartDateTime = (
  startDate: string | null,
  startTime: string | null
): string => mergeDateTimeParts(startDate, startTime);

export const getEndDateTime = (
  endDate: string | null,
  endTime: string | null
): string => mergeDateTimeParts(endDate, endTime);

const mergeDateTimeParts = (
  date: string | null,
  time: string | null
): string => {
  const values: string[] = [];
  if (date) {
    values.push(date);
  }
  if (time) {
    values.push(time);
  }
  return values.join(' ');
};

const isValidTimeFormat = (time: string | null) => {
  if (!time) {
    return false;
  }

  const regex = /^(\d{1,2}):([0-5]\d):([0-5]\d)(?:\s?(AM|PM))?$/i;
  const match = time.match(regex);
  if (!match) {
    return false;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = Number(match[3]);

  if (hours < 0 || hours > 23) {
    return false;
  }
  if (minutes < 0 || minutes > 59) {
    return false;
  }
  if (seconds < 0 || seconds > 59) {
    return false;
  }

  return true;
};

const isValidDateFormat = (date: string) => {
  const regex = /^(?:19|20)\d{2}\-(0[1-9]|1[0-2])\-(0[1-9]|[12]\d|3[01])$/;
  const match = date.match(regex);
  if (!match) {
    return false;
  }
  return true;
};

export type ValidationCode =
  | 'Success'
  | 'Missing required field'
  | 'Invalid range - date'
  | 'Invalid range - time'
  | 'Invalid format - start date'
  | 'Invalid format - start time'
  | 'Invalid format - end date'
  | 'Invalid format - end time';

export interface IValidationResult {
  isValid: boolean;
  validations: ValidationCode[];
}

export const validateStartDate = (
  startDate: string | null
): IValidationResult => {
  const result: IValidationResult = {isValid: false, validations: []};

  // Validate start date format
  const hasValidStart = isValidDateFormat(startDate ?? '');
  if (startDate && !hasValidStart) {
    result.validations.push('Invalid format - start date');
    return result;
  }

  // Start date is required
  const hasStartValue = startDate ? true : false;
  if (!hasStartValue) {
    result.validations.push('Missing required field');
    return result;
  }

  result.isValid = true;
  result.validations = ['Success'];
  return result;
};

export const validateDateRange = (
  startDate: string | null,
  endDate: string | null
): IValidationResult => {
  const result: IValidationResult = {isValid: false, validations: []};

  // Validate start date format
  const hasValidStart = isValidDateFormat(startDate ?? '');
  if (startDate && !hasValidStart) {
    result.validations.push('Invalid format - start date');
  }

  // Validate end date format
  const hasValidEnd = isValidDateFormat(endDate ?? '');
  if (endDate && !hasValidEnd) {
    result.validations.push('Invalid format - end date');
  }

  if (result.validations.length > 0) {
    return result;
  }

  // Start and End dates are required for range
  const hasStartAndEndValues = startDate && endDate ? true : false;
  if (!hasStartAndEndValues) {
    result.validations.push('Missing required field');
    return result;
  }

  // Start date must come before End date
  const start = new Date(startDate!);
  const end = new Date(endDate!);
  const startComesBeforeEnd = end >= start;
  if (!startComesBeforeEnd) {
    result.validations.push('Invalid range - date');
    return result;
  }

  result.isValid = true;
  result.validations = ['Success'];
  return result;
};

export const validateStartTime = (
  startTime: string | null
): IValidationResult => {
  const result: IValidationResult = {isValid: false, validations: []};

  // Validate start time format
  const hasValidStartTime = isValidTimeFormat(startTime ?? '');
  if (startTime && !hasValidStartTime) {
    result.validations.push('Invalid format - start time');
    return result;
  }

  // Start time is required
  const hasStartValue = startTime ? true : false;
  if (!hasStartValue) {
    result.validations.push('Missing required field');
    return result;
  }

  result.isValid = true;
  result.validations = ['Success'];
  return result;
};

export const validateTimeRange = (
  startTime: string | null,
  endTime: string | null
): IValidationResult => {
  const result: IValidationResult = {isValid: false, validations: []};

  // Validate start time format
  const hasValidStartTime = isValidTimeFormat(startTime ?? '');
  if (startTime && !hasValidStartTime) {
    result.validations.push('Invalid format - start time');
  }

  // Validate end time format
  const hasValidEndTime = isValidTimeFormat(endTime ?? '');
  if (endTime && !hasValidEndTime) {
    result.validations.push('Invalid format - end time');
  }

  if (result.validations.length > 0) {
    return result;
  }

  // Start and End times are required for range
  const hasStartAndEndValues = startTime && endTime ? true : false;
  if (!hasStartAndEndValues) {
    result.validations.push('Missing required field');
    return result;
  }

  // Start time must come before End time
  const dateOnly = new Date().toDateString();
  const start = new Date(`${dateOnly} ${startTime}`);
  const end = new Date(`${dateOnly} ${endTime}`);
  const startComesBeforeEnd = end >= start;
  if (!startComesBeforeEnd) {
    result.validations.push('Invalid range - time');
    return result;
  }

  result.isValid = true;
  result.validations = ['Success'];
  return result;
};

export const validateStartDateTime = (
  startDate: string | null,
  startTime: string | null
): IValidationResult => {
  const result: IValidationResult = {isValid: false, validations: []};

  // Validate start date format
  const hasValidStart = isValidDateFormat(startDate ?? '');
  if (startDate && !hasValidStart) {
    result.validations.push('Invalid format - start date');
  }

  // Validate start time format
  const hasValidStartTime = isValidTimeFormat(startTime ?? '');
  if (startTime && !hasValidStartTime) {
    result.validations.push('Invalid format - start time');
  }

  if (result.validations.length > 0) {
    return result;
  }

  // Start date is required
  // Start time is optional
  const hasStartDate = startDate ? true : false;
  if (!hasStartDate) {
    result.validations.push('Missing required field');
    return result;
  }

  result.isValid = true;
  result.validations = ['Success'];
  return result;
};

export const validateDateTimeRange = (
  startDate: string | null,
  startTime: string | null,
  endDate: string | null,
  endTime: string | null
): IValidationResult => {
  const result: IValidationResult = {isValid: false, validations: []};

  // Validate start date format
  const hasValidStart = isValidDateFormat(startDate ?? '');
  if (startDate && !hasValidStart) {
    result.validations.push('Invalid format - start date');
  }

  // Validate end date format
  const hasValidEnd = isValidDateFormat(endDate ?? '');
  if (endDate && !hasValidEnd) {
    result.validations.push('Invalid format - end date');
  }

  // Validate start time format
  const hasValidStartTime = isValidTimeFormat(startTime ?? '');
  if (startTime && !hasValidStartTime) {
    result.validations.push('Invalid format - start time');
  }

  // Validate end time format
  const hasValidEndTime = isValidTimeFormat(endTime ?? '');
  if (endTime && !hasValidEndTime) {
    result.validations.push('Invalid format - end time');
  }

  if (result.validations.length > 0) {
    return result;
  }

  // Start and End dates are required for range
  // Start and End times are optional
  const hasStartAndEndValues = startDate && endDate ? true : false;
  if (!hasStartAndEndValues) {
    result.validations.push('Missing required field');
    return result;
  }

  startTime = startTime ?? '00:00:00';
  const startValue = getStartDateTime(startDate, startTime);
  const start = new Date(startValue);

  endTime = endTime ?? '23:59:59';
  const endValue = getEndDateTime(endDate, endTime);
  const end = new Date(endValue);

  // Start date/time must come before End date/time
  const startComesBeforeEnd = end >= start;
  if (!startComesBeforeEnd) {
    result.validations.push('Invalid range - date');
    return result;
  }

  result.isValid = true;
  result.validations = ['Success'];
  return result;
};
